/**
 * Security Domain Seed Data
 *
 * Contains all seed data for the Security Service (24-security):
 * - SecurityRecommendation (security posture recommendations)
 * - ApiKeyRotation (key rotation history)
 * - KeyRotationPolicy (auto-rotation configuration)
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// SECURITY RECOMMENDATION IDs
// =============================================================================

export const SECURITY_RECOMMENDATION_IDS = {
  // OpsMatrix Capital recommendations
  OPSMATRIX_MFA_NOT_ENABLED: seedId('sec-rec-mfa-not-enabled', 1),
  OPSMATRIX_WEAK_SESSION: seedId('sec-rec-weak-session', 2),
  OPSMATRIX_KEY_ROTATION_OVERDUE: seedId('sec-rec-key-rotation', 3),

  // 1Sharpe Capital recommendations
  SHARPE_SSO_NOT_CONFIGURED: seedId('sec-rec-sso-not-configured', 4),
  SHARPE_EXCESSIVE_PERMISSIONS: seedId('sec-rec-excessive-perms', 5),
} as const;

export type SecurityRecommendationId = (typeof SECURITY_RECOMMENDATION_IDS)[keyof typeof SECURITY_RECOMMENDATION_IDS];

// =============================================================================
// API KEY ROTATION IDs
// =============================================================================

export const API_KEY_ROTATION_IDS = {
  OPSMATRIX_PROD_SCHEDULED: seedId('key-rotation-prod-scheduled', 1),
  OPSMATRIX_STAGING_INCIDENT: seedId('key-rotation-staging-incident', 2),
  SHARPE_PROD_SCHEDULED: seedId('key-rotation-sharpe-prod', 3),
  OPSMATRIX_PROD_COMPLIANCE: seedId('key-rotation-prod-compliance', 4),
} as const;

export type ApiKeyRotationId = (typeof API_KEY_ROTATION_IDS)[keyof typeof API_KEY_ROTATION_IDS];

// =============================================================================
// KEY ROTATION POLICY IDs
// =============================================================================

export const KEY_ROTATION_POLICY_IDS = {
  OPSMATRIX_CAPITAL: seedId('key-policy-opsmatrix', 1),
  ONE_SHARPE_CAPITAL: seedId('key-policy-sharpe', 2),
} as const;

export type KeyRotationPolicyId = (typeof KEY_ROTATION_POLICY_IDS)[keyof typeof KEY_ROTATION_POLICY_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface SecurityRecommendationSeedData {
  id: string;
  businessId: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  remediation: string;
  status: 'active' | 'dismissed' | 'resolved';
  dismissedBy?: string;
  dismissedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  metadata?: Record<string, unknown>;
}

export interface ApiKeyRotationSeedData {
  id: string;
  businessId: string;
  keyId: string;
  keyName: string;
  rotatedAt: string;
  rotatedBy: string;
  reason?: string;
  oldKeyPrefix?: string;
  newKeyPrefix?: string;
}

export interface KeyRotationPolicySeedData {
  id: string;
  businessId: string;
  autoRotationEnabled: boolean;
  rotationIntervalDays: number;
  notifyDaysBeforeExpiry: number;
  lastRotationCheck?: string;
}

// =============================================================================
// SECURITY RECOMMENDATION DATA
// =============================================================================

export const SECURITY_RECOMMENDATIONS: Record<
  keyof typeof SECURITY_RECOMMENDATION_IDS,
  SecurityRecommendationSeedData
> = {
  OPSMATRIX_MFA_NOT_ENABLED: {
    id: SECURITY_RECOMMENDATION_IDS.OPSMATRIX_MFA_NOT_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'mfa_not_enabled',
    severity: 'critical',
    title: 'Multi-Factor Authentication Not Enabled for All Users',
    description:
      'Several user accounts in your organization do not have multi-factor authentication (MFA) enabled. This significantly increases the risk of unauthorized access through compromised credentials.',
    remediation:
      'Navigate to Security Settings > MFA Policy and enable mandatory MFA for all user roles. Notify affected users to set up their preferred MFA method (authenticator app or SMS) within 7 days.',
    status: 'active',
    metadata: { affectedUserCount: 4, totalUsers: 12, complianceFramework: 'SOC2' },
  },
  OPSMATRIX_WEAK_SESSION: {
    id: SECURITY_RECOMMENDATION_IDS.OPSMATRIX_WEAK_SESSION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'weak_session_policy',
    severity: 'medium',
    title: 'Session Timeout Policy Is Too Lenient',
    description:
      'The current session timeout is set to 24 hours, which exceeds the recommended maximum of 8 hours for financial services applications. Long-lived sessions increase the window of opportunity for session hijacking attacks.',
    remediation:
      'Update the session policy in Security Settings > Session Management. Set idle timeout to 30 minutes and absolute timeout to 8 hours. Enable re-authentication for sensitive operations.',
    status: 'dismissed',
    dismissedBy: USER_IDS.RACHEL_MARTINEZ,
    dismissedAt: '2026-01-15T14:30:00Z',
    metadata: { currentTimeoutHours: 24, recommendedTimeoutHours: 8 },
  },
  OPSMATRIX_KEY_ROTATION_OVERDUE: {
    id: SECURITY_RECOMMENDATION_IDS.OPSMATRIX_KEY_ROTATION_OVERDUE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    type: 'key_rotation_overdue',
    severity: 'high',
    title: 'API Key Rotation Overdue by 15 Days',
    description:
      'The production API key "Production API Key" has not been rotated in 105 days, exceeding the configured 90-day rotation policy. Stale API keys increase the risk of credential compromise going undetected.',
    remediation:
      'Rotate the overdue API key immediately via API Keys > Manage Keys. Consider enabling automatic key rotation to prevent future overdue rotations. Update any dependent integrations with the new key.',
    status: 'resolved',
    resolvedBy: USER_IDS.OPS_MANAGER,
    resolvedAt: '2026-02-20T09:15:00Z',
    metadata: { keyName: 'Production API Key', daysSinceLastRotation: 105, policyDays: 90 },
  },
  SHARPE_SSO_NOT_CONFIGURED: {
    id: SECURITY_RECOMMENDATION_IDS.SHARPE_SSO_NOT_CONFIGURED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    type: 'sso_not_configured',
    severity: 'high',
    title: 'Single Sign-On (SSO) Not Configured',
    description:
      'Your organization has not configured Single Sign-On (SSO) with an identity provider. SSO centralizes authentication, reduces password fatigue, and enables consistent access policies across all applications.',
    remediation:
      'Configure SSO in Security Settings > Identity Provider. Fiops supports SAML 2.0 and OIDC providers (Okta, Azure AD, Google Workspace). Contact support for assistance with IdP configuration.',
    status: 'active',
    metadata: { supportedProviders: ['okta', 'azure_ad', 'google_workspace'] },
  },
  SHARPE_EXCESSIVE_PERMISSIONS: {
    id: SECURITY_RECOMMENDATION_IDS.SHARPE_EXCESSIVE_PERMISSIONS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    type: 'excessive_permissions',
    severity: 'low',
    title: 'Users With Excessive Permission Grants Detected',
    description:
      'Two user accounts have been granted admin-level permissions but have not used elevated privileges in the past 60 days. Following the principle of least privilege, unused elevated permissions should be revoked.',
    remediation:
      'Review user permissions in User Management > Permissions. Downgrade unused admin accounts to appropriate role-based access levels. Set up quarterly access reviews to prevent permission drift.',
    status: 'active',
    metadata: { affectedUsers: 2, daysSinceLastElevatedAction: 60 },
  },
};

// =============================================================================
// API KEY ROTATION DATA
// =============================================================================

export const API_KEY_ROTATIONS: Record<keyof typeof API_KEY_ROTATION_IDS, ApiKeyRotationSeedData> = {
  OPSMATRIX_PROD_SCHEDULED: {
    id: API_KEY_ROTATION_IDS.OPSMATRIX_PROD_SCHEDULED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    keyId: seedId('api-key-prod', 1),
    keyName: 'Production API Key',
    rotatedAt: '2026-02-20T09:15:00Z',
    rotatedBy: USER_IDS.OPS_MANAGER,
    reason: 'Scheduled rotation',
    oldKeyPrefix: 'om_live_f8k2',
    newKeyPrefix: 'om_live_m9p4',
  },
  OPSMATRIX_STAGING_INCIDENT: {
    id: API_KEY_ROTATION_IDS.OPSMATRIX_STAGING_INCIDENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    keyId: seedId('api-key-staging', 1),
    keyName: 'Staging API Key',
    rotatedAt: '2026-01-08T16:42:00Z',
    rotatedBy: USER_IDS.RACHEL_MARTINEZ,
    reason: 'Security incident - key exposed in build logs',
    oldKeyPrefix: 'om_test_x3n7',
    newKeyPrefix: 'om_test_q5w1',
  },
  SHARPE_PROD_SCHEDULED: {
    id: API_KEY_ROTATION_IDS.SHARPE_PROD_SCHEDULED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    keyId: seedId('api-key-sharpe-prod', 1),
    keyName: 'Production API Key',
    rotatedAt: '2026-02-01T11:00:00Z',
    rotatedBy: USER_IDS.SHARPE_ADMIN,
    reason: 'Scheduled rotation',
    oldKeyPrefix: 'om_live_j2d8',
    newKeyPrefix: 'om_live_r6t3',
  },
  OPSMATRIX_PROD_COMPLIANCE: {
    id: API_KEY_ROTATION_IDS.OPSMATRIX_PROD_COMPLIANCE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    keyId: seedId('api-key-prod', 1),
    keyName: 'Production API Key',
    rotatedAt: '2025-11-15T08:30:00Z',
    rotatedBy: USER_IDS.SARAH_CHEN,
    reason: 'Compliance audit requirement - SOC2 remediation',
    oldKeyPrefix: 'om_live_a1b3',
    newKeyPrefix: 'om_live_f8k2',
  },
};

// =============================================================================
// KEY ROTATION POLICY DATA
// =============================================================================

export const KEY_ROTATION_POLICIES: Record<keyof typeof KEY_ROTATION_POLICY_IDS, KeyRotationPolicySeedData> = {
  OPSMATRIX_CAPITAL: {
    id: KEY_ROTATION_POLICY_IDS.OPSMATRIX_CAPITAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    autoRotationEnabled: true,
    rotationIntervalDays: 90,
    notifyDaysBeforeExpiry: 14,
    lastRotationCheck: '2026-03-01T00:00:00Z',
  },
  ONE_SHARPE_CAPITAL: {
    id: KEY_ROTATION_POLICY_IDS.ONE_SHARPE_CAPITAL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    autoRotationEnabled: false,
    rotationIntervalDays: 180,
    notifyDaysBeforeExpiry: 7,
    lastRotationCheck: '2026-02-01T00:00:00Z',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllSecurityRecommendations(): SecurityRecommendationSeedData[] {
  return Object.values(SECURITY_RECOMMENDATIONS);
}

export function getRecommendationsByBusiness(businessId: string): SecurityRecommendationSeedData[] {
  return Object.values(SECURITY_RECOMMENDATIONS).filter((r) => r.businessId === businessId);
}

export function getRecommendationsBySeverity(
  severity: SecurityRecommendationSeedData['severity'],
): SecurityRecommendationSeedData[] {
  return Object.values(SECURITY_RECOMMENDATIONS).filter((r) => r.severity === severity);
}

export function getRecommendationsByStatus(
  status: SecurityRecommendationSeedData['status'],
): SecurityRecommendationSeedData[] {
  return Object.values(SECURITY_RECOMMENDATIONS).filter((r) => r.status === status);
}

export function getActiveRecommendations(): SecurityRecommendationSeedData[] {
  return Object.values(SECURITY_RECOMMENDATIONS).filter((r) => r.status === 'active');
}

export function getAllApiKeyRotations(): ApiKeyRotationSeedData[] {
  return Object.values(API_KEY_ROTATIONS);
}

export function getApiKeyRotationsByBusiness(businessId: string): ApiKeyRotationSeedData[] {
  return Object.values(API_KEY_ROTATIONS).filter((r) => r.businessId === businessId);
}

export function getAllKeyRotationPolicies(): KeyRotationPolicySeedData[] {
  return Object.values(KEY_ROTATION_POLICIES);
}

export function getKeyRotationPolicyByBusiness(businessId: string): KeyRotationPolicySeedData | undefined {
  return Object.values(KEY_ROTATION_POLICIES).find((p) => p.businessId === businessId);
}
